/**
 * Bulletproof Menu — platform reliability primitive (Phase 178.5.D).
 *
 * ABOUT
 * ─────
 * A small, opinionated menu primitive that structurally eliminates an entire
 * class of dropdown bugs we observed in production:
 *
 *   • `onBlur` + setTimeout(closeMenu, N) racing with `<a>` click bubble
 *   • Menu unmounting BEFORE the click commits (lost navigation)
 *   • Mobile-tap inconsistencies (Safari focus quirks, hash-routing teardown)
 *   • Full-page reloads on internal nav (no SPA continuity)
 *   • Missing keyboard accessibility (Esc / Enter / Arrow)
 *   • Missing outside-click handling
 *
 * USAGE
 * ─────
 *
 *   <BulletproofMenu onNavigate={({source, destination}) => track(...)}>
 *     <BulletproofMenuTrigger asChild>
 *       <button>Open menu</button>
 *     </BulletproofMenuTrigger>
 *     <BulletproofMenuContent className="absolute right-0 mt-2 w-64 rounded-xl border bg-card shadow-xl">
 *       <DropdownSectionLabel>Public</DropdownSectionLabel>
 *       <DropdownItem href="/arenax/me/bookings" icon={Calendar} testid="...">
 *         My bookings
 *       </DropdownItem>
 *       <DropdownItem onSelect={handleLogout} icon={LogOut} testid="...">
 *         Sign out
 *       </DropdownItem>
 *     </BulletproofMenuContent>
 *   </BulletproofMenu>
 *
 * INTERACTION DOCTRINE
 * ────────────────────
 * Every interaction is bound to `onMouseDown` (and `Enter`/`Space` for kbd
 * users), so the navigation/action commits BEFORE any focus, blur, or
 * unmount cycle.  Outside-click is detected via `pointerdown` on document,
 * which fires BEFORE the click bubble.  The menu therefore closes AFTER
 * navigation commits, not before — operator never sees a "did it click?"
 * dead state.
 *
 * Accessibility:
 *   • role="menu" on content, role="menuitem" on each item
 *   • Esc closes the menu (refocuses the trigger)
 *   • Arrow Down/Up cycles focus through items
 *   • Tab still works (items have tabIndex={0})
 *   • aria-haspopup + aria-expanded on the trigger
 *
 * Analytics:
 *   • Optional `onNavigate({source, destination, capability_context})` on
 *     the root.  Fires BEFORE navigation so it's always recorded.
 */
import {
  cloneElement,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

const MenuContext = createContext(null);

export function useBulletproofMenu() {
  const ctx = useContext(MenuContext);
  if (!ctx) {
    throw new Error('useBulletproofMenu() must be used inside <BulletproofMenu>.');
  }
  return ctx;
}

/**
 * Root wrapper. Owns open/close state, outside-click + Esc handling,
 * optional analytics hook.  Renders a `relative` div by default so a
 * `position:absolute` content panel anchors correctly.
 */
export function BulletproofMenu({
  children,
  onNavigate,
  className = 'relative',
  defaultOpen = false,
  open: controlledOpen,
  onOpenChange,
  capabilityContext = null,
  ...rest
}) {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);

  const setOpen = useCallback((next) => {
    const resolved = typeof next === 'function' ? next(open) : next;
    if (!isControlled) setInternalOpen(resolved);
    if (onOpenChange) {
      try { onOpenChange(resolved); } catch (_) { /* non-fatal */ }
    }
  }, [isControlled, onOpenChange, open]);

  const close = useCallback(() => setOpen(false), [setOpen]);
  const toggle = useCallback(() => setOpen((o) => !o), [setOpen]);

  // Outside-click via `pointerdown` (fires BEFORE click commits so a click
  // on a menu item still gets to run its own onMouseDown handler).
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', handler);
    return () => document.removeEventListener('pointerdown', handler);
  }, [open, setOpen]);

  // Esc to close + refocus the trigger.
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus?.();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, setOpen]);

  const value = {
    open, setOpen, close, toggle,
    wrapperRef, triggerRef, onNavigate, capabilityContext,
  };

  return (
    <MenuContext.Provider value={value}>
      <div ref={wrapperRef} className={className} {...rest}>
        {children}
      </div>
    </MenuContext.Provider>
  );
}

/**
 * Trigger button.  Use `asChild` to wrap a custom button while inheriting
 * the open/close + aria semantics.
 */
export function BulletproofMenuTrigger({
  children,
  asChild = false,
  onOpenCapture,
  onClick,
  ...props
}) {
  const { open, toggle, triggerRef } = useBulletproofMenu();
  const handleClick = (e) => {
    if (onClick) onClick(e);
    if (!open && onOpenCapture) {
      try { onOpenCapture(); } catch (_) { /* non-fatal */ }
    }
    toggle();
  };
  if (asChild) {
    return cloneElement(children, {
      ref: triggerRef,
      onClick: handleClick,
      'aria-haspopup': 'menu',
      'aria-expanded': open,
    });
  }
  return (
    <button
      ref={triggerRef}
      type="button"
      onClick={handleClick}
      aria-haspopup="menu"
      aria-expanded={open}
      {...props}
    >
      {children}
    </button>
  );
}

/**
 * Menu content panel.  Renders nothing when closed.  Listens for
 * ArrowDown/ArrowUp to move focus across menu items.
 */
export function BulletproofMenuContent({ children, className, ...props }) {
  const { open } = useBulletproofMenu();
  const ref = useRef(null);

  useEffect(() => {
    if (!open || !ref.current) return undefined;
    const node = ref.current;
    const handler = (e) => {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      e.preventDefault();
      const items = Array.from(
        node.querySelectorAll('[role="menuitem"]:not([aria-disabled="true"]):not([disabled])')
      );
      if (!items.length) return;
      const active = document.activeElement;
      const idx = items.indexOf(active);
      const nextIdx = e.key === 'ArrowDown'
        ? (idx + 1) % items.length
        : (idx <= 0 ? items.length - 1 : idx - 1);
      items[nextIdx].focus();
    };
    node.addEventListener('keydown', handler);
    return () => node.removeEventListener('keydown', handler);
  }, [open]);

  if (!open) return null;
  return (
    <div ref={ref} role="menu" className={className} {...props}>
      {children}
    </div>
  );
}

/**
 * Section label — non-interactive, visually-distinct group header.
 */
export function DropdownSectionLabel({ children, testid, className }) {
  return (
    <div
      className={className || 'px-4 pt-2.5 pb-1 text-[10px] uppercase tracking-[0.25em] font-bold'}
      style={{
        color: 'hsl(var(--ax-text-faint))',
        borderTop: '1px solid var(--ax-divider)',
      }}
      data-testid={testid}
    >
      {children}
    </div>
  );
}

/**
 * Horizontal divider between groups.
 */
export function DropdownDivider({ testid, className }) {
  return (
    <div
      className={className || 'border-t my-1'}
      style={{ borderColor: 'var(--ax-divider)' }}
      data-testid={testid}
      role="separator"
    />
  );
}

/**
 * Semantic grouping of related items.  Optional — purely a11y hint.
 */
export function DropdownGroup({ children, ...props }) {
  return (
    <div role="group" {...props}>
      {children}
    </div>
  );
}

/**
 * The atom that fixed the production click race.
 *
 * Props:
 *   - href           Internal SPA route (uses useNavigate)
 *   - external       If true with href, uses window.location (cross-app)
 *   - onSelect       Custom action handler (e.g. logout). Mutually exclusive with href.
 *   - icon           Lucide icon component
 *   - iconAccent     Use --ax-accent-cyan for icon (default: muted)
 *   - iconColor      Explicit override (wins over iconAccent)
 *   - trailing       Right-aligned slot (e.g. role badge, "Live" pill)
 *   - testid         data-testid value
 *   - disabled       Greyed-out, not interactive
 *   - loading        Disabled + shows a small "…" indicator
 *   - source         Analytics source tag ("avatar_dropdown" etc.)
 *   - active         Visual active state (for current route)
 *   - destructive    Red text variant (for destructive actions)
 *   - children       Label
 */
export function DropdownItem({
  href,
  external = false,
  onSelect,
  icon: Icon,
  iconAccent = false,
  iconColor,
  trailing,
  testid,
  disabled = false,
  loading = false,
  source,
  active = false,
  destructive = false,
  children,
}) {
  const { close, onNavigate, capabilityContext } = useBulletproofMenu();
  const navigate = useNavigate();
  const inert = disabled || loading;
  const resolvedColor = iconColor
    || (iconAccent ? 'hsl(var(--ax-accent-cyan))' : 'hsl(var(--ax-text-muted))');

  const commit = useCallback((e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (inert) return;
    if (onNavigate) {
      try {
        onNavigate({
          source: source || null,
          destination: href || 'custom_action',
          capability_context: capabilityContext,
        });
      } catch (_) { /* non-fatal */ }
    }
    if (onSelect) {
      try { onSelect(); } catch (_) { /* swallow — caller handles errors */ }
    } else if (href) {
      if (external) {
        window.location.href = href;
      } else {
        navigate(href);
      }
    }
    close();
  }, [inert, source, href, external, onSelect, onNavigate, capabilityContext, close, navigate]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      commit(e);
    }
  };

  const sharedClass = [
    'w-full px-4 py-2 text-xs flex items-center gap-2 text-left transition-colors',
    inert
      ? 'opacity-50 cursor-not-allowed'
      : 'hover:bg-black/[0.04] active:bg-black/[0.07] cursor-pointer',
    active ? 'bg-black/[0.03]' : '',
  ].filter(Boolean).join(' ');

  const textColor = destructive
    ? 'hsl(0 70% 55%)'
    : 'hsl(var(--ax-text))';

  const inner = (
    <>
      {Icon ? (
        <Icon
          className="h-3.5 w-3.5 flex-shrink-0"
          style={{ color: destructive ? textColor : resolvedColor }}
        />
      ) : null}
      <span className="flex-1 truncate">{children}</span>
      {loading ? <span className="text-[9px] opacity-60">…</span> : null}
      {trailing}
    </>
  );

  // If we have an href, render as <a> for a11y benefits (right-click "Open
  // in new tab", middle-click new tab, link previews, etc.) but suppress
  // the native left-click — onMouseDown has already committed navigation
  // by the time click would fire, so we'd double-navigate without this.
  if (href) {
    return (
      <a
        href={external ? href : href}
        onMouseDown={commit}
        onClick={(e) => { if (e.button === 0) e.preventDefault(); }}
        onKeyDown={handleKeyDown}
        role="menuitem"
        tabIndex={inert ? -1 : 0}
        className={sharedClass}
        style={{ color: textColor }}
        data-testid={testid}
        aria-disabled={inert || undefined}
        target={external ? '_self' : undefined}
        rel={external ? 'noopener' : undefined}
      >
        {inner}
      </a>
    );
  }

  return (
    <button
      type="button"
      onMouseDown={commit}
      onKeyDown={handleKeyDown}
      role="menuitem"
      tabIndex={inert ? -1 : 0}
      className={sharedClass}
      style={{ color: textColor }}
      data-testid={testid}
      disabled={inert}
      aria-disabled={inert || undefined}
    >
      {inner}
    </button>
  );
}
